export type WearablesTrajectoryStep = {
  step: string;
  time: number;
};

export type WearablesTrajectory = {
  sequence: string[];
  frequency: number;
  lastUsed: number;
};

type WearablesTrajectoryMemory = {
  recentSteps: WearablesTrajectoryStep[];
  trajectories: WearablesTrajectory[];
};

const KEY = "wearables.trajectory.v3";
const MAX_STEPS = 24;
const MAX_TRAJECTORIES = 18;

function getStorage(): Storage | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }

  return window.localStorage;
}

function getDefaultMemory(): WearablesTrajectoryMemory {
  return {
    recentSteps: [],
    trajectories: [],
  };
}

function readMemory(): WearablesTrajectoryMemory {
  const storage = getStorage();
  const fallback = getDefaultMemory();

  if (!storage) {
    return fallback;
  }

  try {
    const raw = storage.getItem(KEY);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw) as Partial<WearablesTrajectoryMemory>;
    return {
      recentSteps: parsed.recentSteps ?? [],
      trajectories: parsed.trajectories ?? [],
    };
  } catch {
    return fallback;
  }
}

function writeMemory(memory: WearablesTrajectoryMemory): WearablesTrajectoryMemory {
  const storage = getStorage();

  if (!storage) {
    return memory;
  }

  storage.setItem(KEY, JSON.stringify(memory));
  return memory;
}

export function getTrajectoryMemory(): WearablesTrajectoryMemory {
  return readMemory();
}

export function recordTrajectory(step: string): WearablesTrajectoryMemory {
  const current = readMemory();
  const event = { step, time: Date.now() };
  const recentSteps = [...current.recentSteps, event].slice(-MAX_STEPS);
  const nextMap = new Map(
    current.trajectories.map((entry) => [entry.sequence.join(">"), entry]),
  );

  for (let width = 2; width <= 3; width += 1) {
    if (recentSteps.length < width) {
      continue;
    }

    const sequence = recentSteps.slice(-width).map((entry) => entry.step);
    const key = sequence.join(">");
    const existing = nextMap.get(key);
    nextMap.set(key, {
      sequence,
      frequency: (existing?.frequency ?? 0) + 1,
      lastUsed: event.time,
    });
  }

  const trajectories = Array.from(nextMap.values())
    .sort((left, right) => {
      if (right.frequency !== left.frequency) {
        return right.frequency - left.frequency;
      }

      return right.lastUsed - left.lastUsed;
    })
    .slice(0, MAX_TRAJECTORIES);

  return writeMemory({ recentSteps, trajectories });
}

export function findTrajectoryCandidate(prefix: string[]): WearablesTrajectory | null {
  if (prefix.length === 0) {
    return null;
  }

  const { trajectories } = readMemory();
  const prefixKey = prefix.join(">");
  const matches = trajectories.filter((entry) => {
    if (entry.sequence.length !== prefix.length + 1) {
      return false;
    }

    return entry.sequence.slice(0, prefix.length).join(">") === prefixKey;
  });

  if (!matches.length) {
    return null;
  }

  return matches.sort((left, right) => {
    if (right.frequency !== left.frequency) {
      return right.frequency - left.frequency;
    }

    return right.lastUsed - left.lastUsed;
  })[0] ?? null;
}